import * as Vuex from "vuex";
import { Customer, CustomerAPIKey, DeleteCustomer } from "@/entities/customer";
import { client } from "@/lib/client";
import { handleStoreError } from "@/lib/error";

const customersModule: Vuex.Module<CustomerStoreState, {}> = {
  state: {
    loading: false,
    loadingAPIKeys: false,
    customers: [],
    apiKeys: [],
  },
  getters: {
    customersLoading(state) {
      return state.loading;
    },
    customersTable(state) {
      return state.customers;
    },
    apiKeysTable(state) {
      return state.apiKeys;
    },
    apiKeysCustomersLoading(state) {
      return state.loadingAPIKeys;
    },
  },
  mutations: {
    SET_CUSTOMERS(state, payload: Customer[]) {
      state.customers = payload;
    },
    ADD_CUSTOMERS(state, customers: Customer[]) {
      state.customers = state.customers.concat(customers);
    },
    REPLACE_CUSTOMER(state, customer: Customer) {
      const idx = state.customers.findIndex((u) => u.id === customer.id);
      if (idx == -1) {
        return;
      }
      state.customers.splice(idx, 1, customer);
    },
    CUSTOMERS_TOGGLE_LOADING(state) {
      state.loading = !state.loading;
    },
    APIKEYS_CUSTOMERS_TOGGLE_LOADING(state) {
      state.loadingAPIKeys = !state.loadingAPIKeys;
    },
    SET_APIS_KEYS(state, payload: CustomerAPIKey[]) {
      state.apiKeys = payload;
    },
    ADD_CUSTOMERS_APIKEYS(state, newAPIKeys: CustomerAPIKey[]) {
      state.apiKeys = state.apiKeys.concat(newAPIKeys);
    },
    REMOVE_CUSTOMERS_APIKEYS(state, apiKey: string) {
      state.apiKeys = state.apiKeys.filter((t) => t.apikey != apiKey);
    },
  },
  actions: {
    getCustomers: async function getCustomers({ commit }) {
      commit("CUSTOMERS_TOGGLE_LOADING");
      const customers = await client.get("customers").json<Customer[]>();
      if (Array.isArray(customers)) {
        commit("SET_CUSTOMERS", customers);
      }
      commit("CUSTOMERS_TOGGLE_LOADING");
    },
    getAPIKeysByCustomer: async function getAPIKeysByCustomer(
      { commit },
      customerId: number
    ) {
      commit("APIKEYS_CUSTOMERS_TOGGLE_LOADING");
      const apikeys = await client
        .get(`customer/${customerId}/apikeys`)
        .json<CustomerAPIKey[]>();
      if (Array.isArray(apikeys)) {
        commit("SET_APIS_KEYS", apikeys);
      }
      commit("APIKEYS_CUSTOMERS_TOGGLE_LOADING");
    },
    async createAPIKey({ commit }, payload: CreateCustomerAPIKey) {
      const newAPIKey = await client
        .post(`customer/${payload.customerId}/apikey`, { json: payload })
        .json<CustomerAPIKey>()
        .catch(handleStoreError(undefined, true));
      commit("ADD_CUSTOMERS_APIKEYS", [newAPIKey]);
    },
    async deleteAPIKey({ commit }, payload: DeleteCustomerAPIKey) {
      await client
        .delete(`customer/${payload.customerId}/apikey`, { json: payload })
        .json<null>()
        .catch(handleStoreError(undefined, true));
      commit("REMOVE_CUSTOMERS_APIKEYS", payload.apiKey);
    },
    async createCustomer({ commit }, payload: Customer) {
      const newCustomer = await client
        .post("customers", { json: payload })
        .json<Customer>()
        .catch(handleStoreError(undefined, true));
      commit("ADD_USERS", [newCustomer]);
    },
    async updateCustomer({ commit }, customer: Customer) {
      const newCustomer = await client
        .put(`customers`, { json: customer })
        .json<Customer>()
        .catch(handleStoreError(undefined, true));
      commit("REPLACE_USER", newCustomer);
    },
    disableCustomer({ dispatch }, id: number) {
      return client
        .put(`customers/${id}/inactive`)
        .then(() => {
          dispatch("getCustomers");
        })
        .catch(
          handleStoreError({ name: "customers/disableCustomers", payload: id })
        );
    },
    enableCustomer({ dispatch }, id: number) {
      return client
        .put(`customers/${id}/active`)
        .then(() => {
          dispatch("getCustomers");
        })
        .catch(
          handleStoreError({ name: "customers/disableCustomers", payload: id })
        );
    },

    async deleteCustomer({ dispatch }, payload: DeleteCustomer) {
      const addDeletedAt = { deleteAt: payload.date };
      return client
        .put(`customers/${payload.id}/delete`, { json: addDeletedAt })
        .json<Customer>()
        .then(() => {
          dispatch("getCustomers");
        });
    },
  },
  namespaced: true,
};

type CreateCustomerAPIKey = {
  customerId: number;
  expireAt: string;
  permissions: Array<string>;
};

type DeleteCustomerAPIKey = {
  customerId: number;
  apiKey: string;
};

type CustomerStoreState = {
  loading: boolean;
  loadingAPIKeys: boolean;
  customers: Customer[];
  apiKeys: CustomerAPIKey[];
};

export default customersModule;
